import { getMockCoupons, type MockCouponScenario } from '../mock/coupons';

export type RakutenCouponDiscountType = 'percentage' | 'fixed_amount' | 'free_shipping' | string | null;

export interface RakutenCoupon {
  id: number;
  title: string;
  usage_conditions: string;
  description: string;
  discount_type: RakutenCouponDiscountType;
  discount_value: number | null;
  start_at: string;
  end_at: string;
  image_url: string | null;
  destination_url: string;
}

interface RakutenCouponResponse {
  coupons: RakutenCoupon[];
}

export class CouponApiUnavailableError extends Error {
  constructor() {
    super('Coupon catalogue is temporarily unavailable.');
    this.name = 'CouponApiUnavailableError';
  }
}

export class CouponApiError extends Error {
  constructor() {
    super('Could not fetch coupon catalogue.');
    this.name = 'CouponApiError';
  }
}

export interface CouponApiOptions {
  endpoint: string;
  useMock?: boolean;
  mockScenario?: MockCouponScenario;
  fetcher?: typeof fetch;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value));
}

function isRakutenCoupon(value: unknown): value is RakutenCoupon {
  if (!value || typeof value !== 'object') return false;
  const coupon = value as Record<string, unknown>;
  return typeof coupon.id === 'number'
    && Number.isFinite(coupon.id)
    && typeof coupon.title === 'string'
    && typeof coupon.usage_conditions === 'string'
    && typeof coupon.description === 'string'
    && isNullableString(coupon.discount_type)
    && isNullableNumber(coupon.discount_value)
    && typeof coupon.start_at === 'string'
    && typeof coupon.end_at === 'string'
    && isNullableString(coupon.image_url)
    && typeof coupon.destination_url === 'string';
}

function parseResponse(value: unknown): RakutenCouponResponse {
  if (!value || typeof value !== 'object') throw new CouponApiError();
  const coupons = (value as Record<string, unknown>).coupons;
  if (!Array.isArray(coupons) || !coupons.every(isRakutenCoupon)) throw new CouponApiError();
  return { coupons };
}

export async function fetchRakutenCoupons(options: CouponApiOptions): Promise<RakutenCoupon[]> {
  if (options.useMock === true) return getMockCoupons(options.mockScenario ?? 'multiple');
  const endpoint = options.endpoint.trim();
  if (!endpoint) throw new CouponApiError();

  let parsedEndpoint: URL;
  try {
    parsedEndpoint = new URL(endpoint);
  } catch {
    throw new CouponApiError();
  }
  const isLocalHttp = parsedEndpoint.protocol === 'http:' && parsedEndpoint.hostname === 'localhost';
  if (parsedEndpoint.protocol !== 'https:' && !isLocalHttp) {
    throw new CouponApiError();
  }

  const fetcher = options.fetcher ?? fetch;
  let response: Response;
  try {
    response = await fetcher(parsedEndpoint.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch {
    throw new CouponApiError();
  }
  if (response.status === 503) throw new CouponApiUnavailableError();
  if (!response.ok) throw new CouponApiError();

  try {
    return parseResponse(await response.json()).coupons;
  } catch (error) {
    if (error instanceof CouponApiError) throw error;
    throw new CouponApiError();
  }
}
